import React from 'react';
import { Phone, Mail, CheckCircle, AlertCircle, ArrowRight, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import Logo from '../components/Logo';

const Home = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO */}
      <SEO />
      
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-blue-600">CompareBeforeYouBuy</h1>
            </div>
            <a href="tel:08006521866">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-base font-semibold">
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white py-16 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            {/* Live Badge */}
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center gap-2 bg-green-500/20 text-green-400 px-4 py-2 rounded-full border border-green-500/30">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <span className="text-sm font-semibold">LIVE SUPPORT AVAILABLE NOW</span>
              </div>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-6 leading-tight">
              UK Mobile & Internet Support You Can Trust
            </h1>

            {/* Description */}
            <p className="text-xl md:text-2xl text-center text-blue-100 mb-8 leading-relaxed max-w-3xl mx-auto">
              Having trouble with your mobile, SIM card, or broadband? Our independent UK experts are ready to help — fast, friendly, and stress-free.
            </p>

            {/* Yellow Warning Box */}
            <div className="bg-yellow-400 text-gray-900 p-4 rounded-lg mb-8 max-w-2xl mx-auto border-2 border-yellow-500 shadow-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-gray-900 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-bold text-base mb-1">⚠️ Independent Service Notice</p>
                  <p className="text-sm">
                    We are an independent third-party support service — not affiliated with any network provider
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="flex justify-center mb-6">
              <a href="tel:08006521866">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white px-12 py-8 text-2xl font-bold shadow-2xl hover:shadow-blue-500/50 transition-all">
                  <Phone className="w-7 h-7 mr-3" />
                  Call Free: 0800 652 1866
                </Button>
              </a>
            </div>

            {/* Affiliate Disclosure - Subtle */}
            <p className="text-center text-xs text-blue-200/70 max-w-2xl mx-auto">
              This is an independent support and comparison service. We may earn commission from partners. Free from all UK landlines and mobiles.
            </p>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 bg-gray-50 border-y border-gray-200">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-6xl mx-auto">
            <div className="text-center p-4">
              <div className="text-3xl mb-2">🇬🇧</div>
              <p className="text-sm font-semibold text-gray-900">UK-Based Support Team</p>
            </div>
            <div className="text-center p-4">
              <div className="text-3xl mb-2">📞</div>
              <p className="text-sm font-semibold text-gray-900">Freephone 0800 Number</p>
            </div>
            <div className="text-center p-4">
              <div className="text-3xl mb-2">⚡</div>
              <p className="text-sm font-semibold text-gray-900">Fast Response Times</p>
            </div>
            <div className="text-center p-4">
              <div className="text-3xl mb-2">🔒</div>
              <p className="text-sm font-semibold text-gray-900">Safe & Secure Service</p>
            </div>
            <div className="text-center p-4">
              <div className="text-3xl mb-2">⭐</div>
              <p className="text-sm font-semibold text-gray-900">Highly Rated Advisors</p>
            </div>
            <div className="text-center p-4">
              <div className="text-3xl mb-2">📅</div>
              <p className="text-sm font-semibold text-gray-900">7 Days a Week</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">What Can We Help You With?</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our expert advisors handle all types of mobile and internet queries — whatever the issue, we've got you covered.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Service Card 1 */}
              <Card className="border-2 border-gray-200 hover:border-blue-600 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="text-4xl mb-4">📱</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">SIM Card Issues</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    SIM not working? Card not recognised? We'll walk you through SIM activation, replacement requests, and troubleshooting step by step.
                  </p>
                  <a href="tel:08006521866" className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center gap-1">
                    📞 Call for SIM help →
                  </a>
                </CardContent>
              </Card>

              {/* Service Card 2 */}
              <Card className="border-2 border-gray-200 hover:border-blue-600 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="text-4xl mb-4">📶</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Network & Signal Problems</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    No signal, slow data, or dropped calls? Our team can diagnose network outages and signal issues and advise on the best next steps.
                  </p>
                  <a href="tel:08006521866" className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center gap-1">
                    📞 Fix network issues →
                  </a>
                </CardContent>
              </Card>

              {/* Service Card 3 */}
              <Card className="border-2 border-gray-200 hover:border-blue-600 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="text-4xl mb-4">💷</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Billing & Account Queries</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Unexpected charges, billing errors, or account access problems? We'll help you understand your bills and guide you through disputes.
                  </p>
                  <a href="tel:08006521866" className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center gap-1">
                    📞 Billing help →
                  </a>
                </CardContent>
              </Card>

              {/* Service Card 4 */}
              <Card className="border-2 border-gray-200 hover:border-blue-600 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="text-4xl mb-4">🔄</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Upgrades & New Plans</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Looking for a better deal? We provide impartial advice on the best mobile and SIM-only plans available for your needs and budget.
                  </p>
                  <a href="tel:08006521866" className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center gap-1">
                    📞 Plan advice →
                  </a>
                </CardContent>
              </Card>

              {/* Service Card 5 */}
              <Card className="border-2 border-gray-200 hover:border-blue-600 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="text-4xl mb-4">🌐</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Broadband & Internet Support</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Slow internet, router issues, or broadband faults? Our advisors can help diagnose your home internet problems and suggest solutions.
                  </p>
                  <a href="tel:08006521866" className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center gap-1">
                    📞 Broadband help →
                  </a>
                </CardContent>
              </Card>

              {/* Service Card 6 */}
              <Card className="border-2 border-gray-200 hover:border-blue-600 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="text-4xl mb-4">🔢</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Number Porting & Transfers</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Want to keep your number when switching providers? We'll guide you through the PAC code process and make switching hassle-free.
                  </p>
                  <a href="tel:08006521866" className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center gap-1">
                    📞 Port your number →
                  </a>
                </CardContent>
              </Card>

              {/* Service Card 7 */}
              <Card className="border-2 border-gray-200 hover:border-blue-600 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="text-4xl mb-4">⚙️</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Device Setup & Configuration</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    New phone or device not set up correctly? We help with APN settings, data configuration, voicemail setup and more.
                  </p>
                  <a href="tel:08006521866" className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center gap-1">
                    📞 Setup help →
                  </a>
                </CardContent>
              </Card>

              {/* Service Card 8 */}
              <Card className="border-2 border-gray-200 hover:border-blue-600 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="text-4xl mb-4">🚨</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Lost, Stolen & Blocked Phones</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Phone lost or stolen? We'll advise you on what steps to take, including how to bar your device and protect your account.
                  </p>
                  <a href="tel:08006521866" className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center gap-1">
                    📞 Get urgent help →
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Get Help in 3 Simple Steps</h2>
              <p className="text-xl text-gray-600">
                No long waits, no complicated processes. Just fast, friendly support when you need it most.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  1
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Call Our Free Number</h3>
                <p className="text-gray-600">
                  Dial <strong>0800 652 1866</strong> — it's completely free from any UK landline or mobile. No automated menus.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  2
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Speak to an Expert</h3>
                <p className="text-gray-600">
                  A friendly, knowledgeable advisor will listen to your issue and ask a few simple questions to understand your situation.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  3
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Problem Solved</h3>
                <p className="text-gray-600">
                  We'll walk you through the solution step by step — most issues are resolved in a single call.
                </p>
              </div>
            </div>

            <div className="text-center">
              <a href="tel:08006521866">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Free Now: 0800 652 1866
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Common Issues */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Issues We Resolve Every Day</h2>
              <p className="text-xl text-gray-600">
                From the simple to the complex, our team has the knowledge and experience to help.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                <CheckCircle className="w-8 h-8 text-green-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">SIM Not Recognised</h3>
                <p className="text-sm text-gray-600">Phone showing no SIM, SIM card error, or SIM not reading.</p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                <CheckCircle className="w-8 h-8 text-green-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Can't Make or Receive Calls</h3>
                <p className="text-sm text-gray-600">Calls failing, going straight to voicemail or network errors.</p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                <CheckCircle className="w-8 h-8 text-green-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Mobile Data Not Working</h3>
                <p className="text-sm text-gray-600">No internet on mobile data, very slow speeds or 4G/5G issues.</p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                <CheckCircle className="w-8 h-8 text-green-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Unexpected Bill Charges</h3>
                <p className="text-sm text-gray-600">Charges you don't recognise, premium rate calls or billing errors.</p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                <CheckCircle className="w-8 h-8 text-green-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Account Locked or Suspended</h3>
                <p className="text-sm text-gray-600">Account access problems, payment issues or service suspension.</p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                <CheckCircle className="w-8 h-8 text-green-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Switching Network Provider</h3>
                <p className="text-sm text-gray-600">Want to switch networks but don't know how to keep your number.</p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                <CheckCircle className="w-8 h-8 text-green-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Roaming Problems Abroad</h3>
                <p className="text-sm text-gray-600">Phone not working overseas, high roaming charges, SIM issues abroad.</p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                <CheckCircle className="w-8 h-8 text-green-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Broadband Going Slow</h3>
                <p className="text-sm text-gray-600">Slow speeds, frequent dropouts or internet not connecting at home.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-12 bg-blue-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Need Help Right Now?</h2>
            <p className="text-xl mb-6">
              Don't waste hours on hold. Our UK advisors are ready to help — call our free helpline now.
            </p>
            <a href="tel:08006521866">
              <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-xl font-bold">
                <Phone className="w-6 h-6 mr-2" />
                0800 652 1866
              </Button>
            </a>
            <p className="text-sm mt-4 text-blue-100">
              Free from all UK landlines and mobiles · Independent third-party support
            </p>
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Customers Say</h2>
              <p className="text-xl text-gray-600">
                Real feedback from people we've helped with their mobile and internet issues.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-2 border-gray-200">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  </div>
                  <p className="text-gray-700 mb-4 italic">
                    "My SIM stopped working and I had no idea what to do. Called this number and the advisor sorted everything within 20 minutes. Brilliant service!"
                  </p>
                  <p className="text-sm font-semibold text-gray-900">— Sarah T., Manchester</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-200">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  </div>
                  <p className="text-gray-700 mb-4 italic">
                    "Had a billing dispute I couldn't resolve myself. The team were patient, clear and really helpful. Would definitely recommend."
                  </p>
                  <p className="text-sm font-semibold text-gray-900">— James R., Birmingham</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-200">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  </div>
                  <p className="text-gray-700 mb-4 italic">
                    "Helped me switch networks without losing my number. The advisor explained every step clearly. Fast and friendly — 5 stars!"
                  </p>
                  <p className="text-sm font-semibold text-gray-900">— Priya M., London</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            </div>

            <div className="space-y-6">
              <Card className="border-2 border-gray-200">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Are you affiliated with O2, EE, Vodafone or any network?
                  </h3>
                  <p className="text-gray-700">
                    No. We are a completely independent third-party mobile and internet support service. We are not affiliated with, endorsed by, or connected to O2, EE, Vodafone, Three, Sky, Tesco Mobile, or any other network provider.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-200">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Is calling 0800 652 1866 really free?
                  </h3>
                  <p className="text-gray-700">
                    Yes. 0800 numbers are free to call from all UK landlines and mobile phones. You will not be charged for calling our helpline.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-200">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    What networks can you help with?
                  </h3>
                  <p className="text-gray-700">
                    We provide general mobile and internet support guidance for customers on any UK network. We can advise you on how to resolve issues and direct you to the right resources, regardless of which network or broadband provider you use.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-200">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    When are your advisors available?
                  </h3>
                  <p className="text-gray-700">
                    Our helpline is available 7 days a week. We aim to have advisors ready to take your call during standard UK business hours and beyond.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-200">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Can you make changes to my account directly?
                  </h3>
                  <p className="text-gray-700">
                    As an independent third-party support service, we do not have direct access to any network or provider accounts. We provide expert guidance, advice, and step-by-step help to resolve your issues.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              {/* Brand */}
              <div>
                <p className="text-2xl font-bold mb-4">COMPARE BEFORE YOU BUY</p>
                <p className="text-gray-400 mb-4">Independent UK Mobile & Internet Support</p>
                <div className="mt-6">
                  <a href="tel:08006521866" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
                    <Phone className="w-5 h-5" />
                    0800 652 1866
                  </a>
                </div>
              </div>

              {/* Contact */}
              <div>
                <h3 className="text-lg font-bold mb-4">Contact Us</h3>
                <ul className="space-y-3 text-gray-400">
                  <li>
                    <a href="tel:08006521866" className="hover:text-white transition-colors flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      0800 652 1866 (Free)
                    </a>
                  </li>
                  <li>
                    <a href="mailto:contact@comparebeforeyoubuy.shop" className="hover:text-white transition-colors flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      contact@comparebeforeyoubuy.shop
                    </a>
                  </li>
                  <li className="text-sm">Available 7 Days a Week</li>
                </ul>
              </div>

              {/* Legal */}
              <div>
                <h3 className="text-lg font-bold mb-4">Legal</h3>
                <ul className="space-y-2">
                  <li><a href="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</a></li>
                  <li><a href="/terms-conditions" className="text-gray-400 hover:text-white transition-colors">Terms of Service</a></li>
                </ul>
              </div>
            </div>

            {/* Important Disclaimer */}
            <div className="border-t border-gray-800 pt-8 mb-8">
              <div className="bg-gray-800 p-6 rounded-lg">
                <h4 className="text-white font-bold mb-3 text-lg">Important Disclaimer</h4>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Compare Before You Buy is a completely independent third-party mobile and internet support service. We are not affiliated with, endorsed by, or connected to O2, EE, Vodafone, Three, Sky, Tesco Mobile, or any other network provider. We provide general mobile and internet support guidance for customers on any UK network. We can advise you on how to resolve issues and direct you to the right resources, regardless of which network or broadband provider you use.
                </p>
              </div>
            </div>

            {/* Copyright */}
            <div className="text-center">
              <p className="text-gray-400 text-sm">
                © 2025 Compare Before You Buy. All rights reserved. Independent third-party mobile and internet support service.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Sticky Footer Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-blue-600 text-white py-4 px-4 shadow-2xl z-50">
        <div className="container mx-auto">
          <div className="flex items-center justify-center gap-4">
            <span className="text-lg font-semibold hidden sm:inline">Need Help Now?</span>
            <a href="tel:08006521866">
              <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-xl font-bold shadow-lg">
                <Phone className="w-5 h-5 mr-2" />
                Call Free: 0800 652 1866
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
